/**
 * NF Drawer Swipe — swipe-to-close gestures for mobile drawers.
 *
 * Menu drawer : swipe LEFT  → close (panel slides in from the left).
 * Cart drawer : swipe RIGHT → close (panel slides in from the right).
 *
 * Only active on touch-capable devices.
 * Vertical scrolling inside drawers is never interrupted.
 *
 * Implementation notes
 * ────────────────────
 * Menu drawer uses CSS transitions (transform/visibility/opacity controlled by
 * .menu-open class). Setting `transition: none` inline before calling close()
 * makes getAnimations() return [] → onAnimationEnd resolves immediately →
 * reset() hides the panel. We clear the inline override in the next frame
 * (panel is visibility:hidden by then, so no flash).
 *
 * Cart drawer uses the theme's --dialog-drawer-closing-animation custom
 * property (same pattern used by #handlePopState in cart-drawer.js) to
 * suppress the native close animation when we've already animated the inner
 * panel off-screen ourselves.
 */
(function () {
  if (!('ontouchstart' in window) && navigator.maxTouchPoints < 1) return;

  /* ── Constants ─────────────────────────────────────────────────────── */
  var CLOSE_PX     = 80;   // minimum horizontal travel to trigger close
  var FLICK_PX_MS  = 0.45; // px/ms — fast flick also closes below CLOSE_PX
  var FLICK_MIN_PX = 35;   // minimum travel even for a fast flick
  var DIR_PX       = 8;    // travel before direction is locked (H vs V)

  /* ─────────────────────────────────────────────────────────────────────
     MENU DRAWER  (slides in from the LEFT, swipe LEFT to close)
     ───────────────────────────────────────────────────────────────────── */
  function initMenuDrawerSwipe() {
    var drawerEl = document.querySelector('header-drawer');
    if (!drawerEl) return;

    var panel = drawerEl.querySelector('.menu-drawer');
    if (!panel) return;

    var startX = 0, startY = 0, startTime = 0;
    var curDX  = 0;
    var locked = null;   // null | 'h' | 'v'
    var active = false;

    function isOpen() {
      var det = drawerEl.querySelector('details.menu-drawer-container');
      return det ? det.hasAttribute('open') : false;
    }

    panel.addEventListener('touchstart', function (e) {
      if (!isOpen()) return;
      startX    = e.touches[0].clientX;
      startY    = e.touches[0].clientY;
      startTime = Date.now();
      curDX     = 0;
      locked    = null;
      active    = true;
      panel.style.transition = 'none';
    }, { passive: true });

    panel.addEventListener('touchmove', function (e) {
      if (!active) return;

      var dx = e.touches[0].clientX - startX;
      var dy = e.touches[0].clientY - startY;

      if (locked === null) {
        if (Math.abs(dx) + Math.abs(dy) < DIR_PX) return;
        locked = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
      }

      if (locked === 'v') {
        active = false;
        panel.style.transition = '';
        panel.style.transform  = '';
        return;
      }

      /* Only track leftward pull (negative delta = toward close) */
      curDX = Math.min(0, dx);
      panel.style.transform = 'translateX(' + curDX + 'px)';
      e.preventDefault();
    }, { passive: false });

    panel.addEventListener('touchend', function (e) {
      if (!active || locked !== 'h') { resetPanel(); return; }

      var finalDX  = e.changedTouches[0].clientX - startX;
      var elapsed  = Date.now() - startTime || 1;
      var velocity = Math.abs(finalDX) / elapsed;
      active       = false;

      var close = finalDX < -CLOSE_PX ||
                  (finalDX < -FLICK_MIN_PX && velocity > FLICK_PX_MS);

      if (close) {
        /* Animate panel fully off-screen to the left … */
        var width = panel.offsetWidth;
        panel.style.transition = 'transform 0.18s ease-out';
        panel.style.transform  = 'translateX(-' + width + 'px)';

        panel.addEventListener('transitionend', function handler() {
          panel.removeEventListener('transitionend', handler);
          panel.style.transition = 'none';  /* suppress CSS close transition */
          drawerEl.close();                 /* reset() fires immediately */
          /* Restore after panel is hidden (visibility:hidden by CSS) */
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              panel.style.transition = '';
              panel.style.transform  = '';
            });
          });
        });
      } else {
        snapBackPanel();
      }
    }, { passive: true });

    panel.addEventListener('touchcancel', resetPanel, { passive: true });

    function resetPanel() {
      active = false;
      panel.style.transition = '';
      panel.style.transform  = '';
    }

    function snapBackPanel() {
      panel.style.transition = 'transform 0.22s ease-out';
      panel.style.transform  = '';
      panel.addEventListener('transitionend', function handler() {
        panel.removeEventListener('transitionend', handler);
        panel.style.transition = '';
      });
    }
  }


  /* ─────────────────────────────────────────────────────────────────────
     CART DRAWER  (slides in from the RIGHT, swipe RIGHT to close)
     We translate .cart-drawer__inner (not <dialog>) so that the CSS
     @keyframes animation on the dialog element cannot override our inline
     transform via the animation cascade layer (animations beat inline
     styles in CSS priority).  The dialog's background is made transparent
     during the gesture so the ::backdrop (dimmed page) shows through as
     the panel slides away.  .cart-drawer__inner carries its own
     background-color so the cart content stays opaque throughout.
     ───────────────────────────────────────────────────────────────────── */
  function initCartDrawerSwipe() {
    var cartEl = document.querySelector('cart-drawer-component');
    if (!cartEl) return;

    var dialog = cartEl.querySelector('dialog.cart-drawer__dialog');
    if (!dialog) return;

    var inner = dialog.querySelector('.cart-drawer__inner');
    if (!inner) return;

    var startX = 0, startY = 0, startTime = 0;
    var curDX  = 0;
    var locked = null;
    var active = false;

    inner.addEventListener('touchstart', function (e) {
      if (!dialog.open) return;
      /* Skip if the opening animation is still in flight */
      var anims = dialog.getAnimations({ subtree: false });
      if (anims.some(function (a) { return a.playState === 'running'; })) return;

      startX    = e.touches[0].clientX;
      startY    = e.touches[0].clientY;
      startTime = Date.now();
      curDX     = 0;
      locked    = null;
      active    = true;
      /* Transparent dialog reveals the ::backdrop (dimmed page) behind inner */
      dialog.style.background = 'transparent';
      inner.style.transition  = 'none';
    }, { passive: true });

    inner.addEventListener('touchmove', function (e) {
      if (!active) return;

      var dx = e.touches[0].clientX - startX;
      var dy = e.touches[0].clientY - startY;

      if (locked === null) {
        if (Math.abs(dx) + Math.abs(dy) < DIR_PX) return;
        locked = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
      }

      if (locked === 'v') {
        active = false;
        dialog.style.background = '';
        inner.style.transition  = '';
        inner.style.transform   = '';
        return;
      }

      /* Only track rightward pull (positive delta = toward close) */
      curDX = Math.max(0, dx);
      inner.style.transform = 'translateX(' + curDX + 'px)';
      e.preventDefault();
    }, { passive: false });

    inner.addEventListener('touchend', function (e) {
      if (!active || locked !== 'h') { resetInner(); return; }

      var finalDX  = e.changedTouches[0].clientX - startX;
      var elapsed  = Date.now() - startTime || 1;
      var velocity = Math.abs(finalDX) / elapsed;
      active       = false;

      var close = finalDX > CLOSE_PX ||
                  (finalDX > FLICK_MIN_PX && velocity > FLICK_PX_MS);

      if (close) {
        /* Animate inner panel off-screen to the right */
        var width = inner.offsetWidth;
        inner.style.transition = 'transform 0.18s ease-out';
        inner.style.transform  = 'translateX(' + width + 'px)';

        inner.addEventListener('transitionend', function handler() {
          inner.removeEventListener('transitionend', handler);
          inner.style.transition = '';

          /*
           * Suppress the dialog's own CSS close animation — inner is
           * already off-screen so no further animation is needed.
           */
          dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
          cartEl.close();

          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              dialog.style.removeProperty('--dialog-drawer-closing-animation');
              dialog.style.background = '';
              inner.style.transform   = '';
            });
          });
        });
      } else {
        snapBackInner();
      }
    }, { passive: true });

    inner.addEventListener('touchcancel', resetInner, { passive: true });

    function resetInner() {
      active = false;
      inner.style.transition  = '';
      inner.style.transform   = '';
      dialog.style.background = '';
    }

    function snapBackInner() {
      inner.style.transition = 'transform 0.22s ease-out';
      inner.style.transform  = '';
      inner.addEventListener('transitionend', function handler() {
        inner.removeEventListener('transitionend', handler);
        inner.style.transition  = '';
        dialog.style.background = '';
      });
    }
  }


  /* ── Init ──────────────────────────────────────────────────────────── */
  function init() {
    initMenuDrawerSwipe();
    initCartDrawerSwipe();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
